import { useEffect, useRef } from 'react';

const products = [
  {
    id: 'regular',
    icon: 'ri-archive-line',
    title: 'REGULAR CAN',
    price: '$50.00',
    unit: 'per can',
    features: ['Custom Giants or 49ers logo', 'Holds over 30 pouches', 'Aerospace-grade aluminum construction', 'High-quality anodized coating', 'Ergonomic cutouts for easy open & close', 'Airtight seal to keep pouches fresh'],
    accent: '#CE1126',
  },
  {
    id: 'trash',
    icon: 'ri-stack-line',
    title: 'TRASH CAN',
    price: '$75.00',
    unit: 'per can',
    features: ['Custom Giants or 49ers logo', 'Separate compartments for fresh & used product', 'No need to search for a trash can', 'Aerospace-grade aluminum construction', 'High-quality anodized coating', 'Airtight seal to keep pouches fresh'],
    accent: '#CE1126',
  },
  {
    id: 'pouches',
    icon: 'ri-capsule-line',
    title: 'POUCHES',
    price: '$7.50',
    unit: 'per can',
    features: ['Brands: ZYN / VELO / ALPS', 'Strengths: 3mg / 6mg / 9mg', 'All flavors available', 'Order standalone or add to any can'],
    accent: '#D4FF00',
  },
];

const ProductCard = ({ product, index }: { product: typeof products[0]; index: number }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              if (cardRef.current) {
                cardRef.current.style.opacity = '1';
                cardRef.current.style.transform = 'translateY(0)';
              }
            }, index * 150);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      className="flex-1 min-w-0 border border-[#3a3a3a] p-6 md:p-8 group cursor-pointer transition-all duration-300 hover:-translate-y-2"
      style={{
        background: product.id === 'pouches' ? 'linear-gradient(160deg, #2a2a1a 0%, #252515 100%)' : 'linear-gradient(160deg, #2e2e2e 0%, #252525 100%)',
        borderTop: `3px solid ${product.accent}`,
        opacity: 0,
        transform: 'translateY(40px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 30px rgba(206,17,38,0.25)`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      {/* Icon */}
      <div className="w-16 h-16 flex items-center justify-center rounded-full mb-6 mx-auto" style={{ background: '#D4FF00' }}>
        <i className={`${product.icon} text-2xl text-black`} />
      </div>

      {/* Title */}
      <h3
        className="text-2xl md:text-3xl text-center text-white mb-2 tracking-widest uppercase"
        style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
      >
        {product.title}
      </h3>

      {/* Price */}
      <div className="text-center mb-6">
        <span
          className="text-4xl md:text-5xl font-bold"
          style={{ fontFamily: '"Courier New", monospace', color: product.accent }}
        >
          {product.price}
        </span>
        <span className="text-[#888] font-mono text-sm ml-2">/ {product.unit}</span>
      </div>

      {/* Features */}
      <ul className="space-y-2 mb-8">
        {product.features.map((f) => (
          <li key={f} className="flex items-start gap-2 font-mono text-sm text-[#ccc]">
            <span className="mt-0.5 shrink-0" style={{ color: product.accent }}>▸</span>
            {f}
          </li>
        ))}
      </ul>

      {/* Bottom accent */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#CE1126]/50 to-transparent" />
    </div>
  );
};

const ProductShowcase = ({ onOrderClick }: { onOrderClick: () => void }) => {
  const sectionRef = useRef<HTMLDivElement>(null);

  return (
    <section
      id="products"
      ref={sectionRef}
      className="py-20 md:py-28 px-4 md:px-8"
      style={{
        background: '#1e1e1e',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='4' height='4' fill='%231e1e1e'/%3E%3Crect x='0' y='0' width='1' height='1' fill='%23222'/%3E%3Crect x='2' y='2' width='1' height='1' fill='%23222'/%3E%3C/svg%3E")`,
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="mb-14 text-center">
          <p className="text-[#D4FF00] font-mono text-xs tracking-[0.4em] uppercase mb-3">Station Supply Catalog</p>
          <h2
            className="text-4xl md:text-6xl text-white uppercase tracking-widest"
            style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
          >
            PRODUCT LINEUP
          </h2>
          <div className="mt-4 h-px max-w-xs mx-auto bg-gradient-to-r from-transparent via-[#CE1126] to-transparent" />
        </div>

        {/* Cards */}
        <div className="flex flex-col md:flex-row gap-6">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <button
            onClick={onOrderClick}
            className="px-10 py-4 bg-[#CE1126] border border-[#C0C0C0] text-white font-black uppercase tracking-[0.25em] text-base transition-all duration-200 hover:-translate-y-1 active:scale-95 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: '"Black Ops One", "Impact", sans-serif' }}
          >
            DISPATCH ORDER
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
